import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  fetchProjectsForPlaneamiento,
  nombreProyectoDesdeLista,
} from '../../../utils/projectsFromApi';
import { formatearRangoFechas } from '../../../data/mockCiclosCiclicos';
import {
  listarFilasSalidasDelDia,
  ESTADO_SALIDA,
  ESTADO_LABEL,
  formatearDiaCorto,
} from '../../../data/mockSalidasProduccion';
import { fetchPlantillasCiclicas } from '../../../services/plantillaCiclicaService';
import { fetchCiclicosList } from '../../../services/ciclicoService';

const basePath = '/produccion-concesionaria/operaciones/despacho';

function BadgeEstado() {
  return (
    <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
      {ESTADO_LABEL[ESTADO_SALIDA.SALIDA_PRODUCCION]}
    </span>
  );
}

const DespachoIndex = () => {
  const location = useLocation();
  const [plantillasCatalog, setPlantillasCatalog] = useState([]);
  const [cargandoPlantillas, setCargandoPlantillas] = useState(true);
  const [ciclosApi, setCiclosApi] = useState([]);
  const [cargandoCiclos, setCargandoCiclos] = useState(true);

  const filas = useMemo(
    () => listarFilasSalidasDelDia(plantillasCatalog, ciclosApi),
    [location.key, plantillasCatalog, ciclosApi],
  );

  const filasSoloSalidaProduccion = useMemo(
    () => filas.filter((r) => r.estado === ESTADO_SALIDA.SALIDA_PRODUCCION),
    [filas],
  );

  const [proyectosCatalog, setProyectosCatalog] = useState([]);

  useEffect(() => {
    let ok = true;
    (async () => {
      const list = await fetchProjectsForPlaneamiento();
      if (ok) setProyectosCatalog(list);
    })();
    return () => {
      ok = false;
    };
  }, []);

  useEffect(() => {
    let ok = true;
    setCargandoCiclos(true);
    (async () => {
      try {
        const list = await fetchCiclicosList();
        if (ok) setCiclosApi(list);
      } catch {
        if (ok) setCiclosApi([]);
      } finally {
        if (ok) setCargandoCiclos(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, [location.key]);

  useEffect(() => {
    let ok = true;
    setCargandoPlantillas(true);
    (async () => {
      try {
        const list = await fetchPlantillasCiclicas();
        if (ok) setPlantillasCatalog(list);
      } catch {
        if (ok) setPlantillasCatalog([]);
      } finally {
        if (ok) setCargandoPlantillas(false);
      }
    })();
    return () => {
      ok = false;
    };
  }, []);

  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroProyectoId, setFiltroProyectoId] = useState('');
  const [filtroAnio, setFiltroAnio] = useState('');
  const [filtroNumeroSemana, setFiltroNumeroSemana] = useState('');

  const grupos = useMemo(() => {
    const ciclos = ciclosApi;
    const map = new Map();
    for (const row of filasSoloSalidaProduccion) {
      if (!map.has(row.cicloId)) {
        map.set(row.cicloId, {
          cicloId: row.cicloId,
          cicloNombre: row.cicloNombre,
          semanaLabel: row.semanaLabel,
          dias: [],
        });
      }
      map.get(row.cicloId).dias.push(row);
    }
    const list = Array.from(map.values()).map((g) => {
      const c = ciclos.find((x) => String(x.id) === String(g.cicloId));
      const diasOrdenados = [...g.dias].sort((a, b) =>
        String(b.fechaYmd).localeCompare(String(a.fechaYmd)),
      );
      const proyectoId = c?.proyectoId != null ? String(c.proyectoId) : '';
      return {
        ...g,
        dias: diasOrdenados,
        periodoLabel: c ? formatearRangoFechas(c.fechaInicio, c.fechaFin) : '—',
        proyectoId,
        proyectoNombre: nombreProyectoDesdeLista(proyectoId, proyectosCatalog),
        anio: c?.anio,
        numeroSemana: c?.numeroSemana,
      };
    });
    list.sort((a, b) => {
      const fa = a.dias[0]?.fechaYmd || '';
      const fb = b.dias[0]?.fechaYmd || '';
      return fb.localeCompare(fa);
    });
    return list;
  }, [filasSoloSalidaProduccion, proyectosCatalog, ciclosApi]);

  const gruposFiltrados = useMemo(() => {
    const q = filtroTexto.trim().toLowerCase();
    return grupos.filter((g) => {
      if (q && !String(g.cicloNombre || '').toLowerCase().includes(q)) return false;
      if (filtroProyectoId && String(g.proyectoId || '') !== String(filtroProyectoId)) return false;
      if (filtroAnio) {
        const y = parseInt(filtroAnio, 10);
        if (!Number.isNaN(y) && g.anio !== y) return false;
      }
      if (filtroNumeroSemana) {
        const w = parseInt(filtroNumeroSemana, 10);
        if (!Number.isNaN(w) && g.numeroSemana !== w) return false;
      }
      return true;
    });
  }, [grupos, filtroTexto, filtroProyectoId, filtroAnio, filtroNumeroSemana]);

  const aniosEnDatos = useMemo(() => {
    const s = new Set(grupos.map((g) => g.anio).filter((n) => n != null));
    return [...s].sort((a, b) => b - a);
  }, [grupos]);

  const semanasEnDatos = useMemo(() => {
    const s = new Set(grupos.map((g) => g.numeroSemana).filter((n) => n != null));
    return [...s].sort((a, b) => a - b);
  }, [grupos]);

  const hayFiltrosActivos =
    Boolean(filtroTexto.trim()) ||
    Boolean(filtroProyectoId) ||
    Boolean(filtroAnio) ||
    Boolean(filtroNumeroSemana);

  const limpiarFiltros = () => {
    setFiltroTexto('');
    setFiltroProyectoId('');
    setFiltroAnio('');
    setFiltroNumeroSemana('');
  };

  const [abiertos, setAbiertos] = useState(() => new Set());

  const toggleCiclo = (cicloId) => {
    setAbiertos((prev) => {
      const n = new Set(prev);
      if (n.has(cicloId)) n.delete(cicloId);
      else n.add(cicloId);
      return n;
    });
  };

  const expandirTodos = () => {
    setAbiertos(new Set(gruposFiltrados.map((g) => g.cicloId)));
  };

  const contraerTodos = () => {
    setAbiertos(new Set());
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full max-w-[1600px] mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-600">Operaciones</span>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Despacho</span>
      </nav>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold text-gray-800">Despacho</h1>
          <p className="mt-1 text-sm text-gray-500">
            Solo aparecen los <strong>días con salida a producción</strong> confirmada. Elija un <strong>cíclico</strong>{' '}
            (con su <strong>proyecto</strong>), despliegue <strong>Ver días</strong> y en cada día use{' '}
            <strong>Ver detalle</strong> para repartir las cantidades entre las <strong>áreas de despacho</strong> del
            mismo proyecto.
          </p>
        </div>
        {grupos.length > 0 && (
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={expandirTodos}
              className="text-xs font-medium text-teal-700 hover:underline"
            >
              Expandir todos
            </button>
            <span className="text-gray-300">|</span>
            <button
              type="button"
              onClick={contraerTodos}
              className="text-xs font-medium text-gray-600 hover:underline"
            >
              Contraer todos
            </button>
          </div>
        )}
      </div>

      {cargandoCiclos || cargandoPlantillas ? (
        <p className="mt-8 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          Cargando cíclicos y plantillas…
        </p>
      ) : filas.length === 0 ? (
        <p className="mt-8 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          No hay cíclicos con plantilla o no hay días activos. Cree un cíclico en{' '}
          <Link to="/produccion-concesionaria/planeamiento/ciclicos" className="font-medium underline">
            Planeamiento → Cíclicos
          </Link>
          .
        </p>
      ) : filasSoloSalidaProduccion.length === 0 ? (
        <p className="mt-8 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          Aún no hay días con <strong>salida a producción</strong>. Registre y confirme la salida en{' '}
          <Link
            to="/produccion-concesionaria/operaciones/salidas-produccion"
            className="font-medium text-teal-700 underline"
          >
            Operaciones → Salidas a producción
          </Link>
          .
        </p>
      ) : (
        <>
          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <span className="text-sm font-medium text-gray-700">Filtros</span>
              {grupos.length > 0 && (
                <span className="text-xs text-gray-500">
                  Mostrando <strong className="text-gray-700">{gruposFiltrados.length}</strong> de {grupos.length}{' '}
                  cíclicos
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label htmlFor="filtro-desp-nombre" className="block text-xs font-medium text-gray-600 mb-1">
                  Buscar por nombre
                </label>
                <input
                  id="filtro-desp-nombre"
                  type="text"
                  value={filtroTexto}
                  onChange={(e) => setFiltroTexto(e.target.value)}
                  placeholder="Nombre del cíclico…"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                />
              </div>
              <div>
                <label htmlFor="filtro-desp-proyecto" className="block text-xs font-medium text-gray-600 mb-1">
                  Proyecto
                </label>
                <select
                  id="filtro-desp-proyecto"
                  value={filtroProyectoId}
                  onChange={(e) => setFiltroProyectoId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                >
                  <option value="">Todos</option>
                  {proyectosCatalog.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code ? `${p.code} — ${p.name}` : p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="filtro-desp-anio" className="block text-xs font-medium text-gray-600 mb-1">
                  Año
                </label>
                <select
                  id="filtro-desp-anio"
                  value={filtroAnio}
                  onChange={(e) => setFiltroAnio(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                >
                  <option value="">Todos</option>
                  {aniosEnDatos.map((y) => (
                    <option key={y} value={String(y)}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="filtro-desp-semana" className="block text-xs font-medium text-gray-600 mb-1">
                  N.º semana
                </label>
                <select
                  id="filtro-desp-semana"
                  value={filtroNumeroSemana}
                  onChange={(e) => setFiltroNumeroSemana(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                >
                  <option value="">Todas</option>
                  {semanasEnDatos.map((w) => (
                    <option key={w} value={String(w)}>
                      {w}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {hayFiltrosActivos && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={limpiarFiltros}
                  className="text-sm text-teal-700 font-medium hover:underline"
                >
                  Limpiar filtros
                </button>
              </div>
            )}
          </div>

          {gruposFiltrados.length === 0 ? (
            <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-600">
              Ningún cíclico coincide con los filtros.{' '}
              <button type="button" onClick={limpiarFiltros} className="text-teal-700 font-medium hover:underline">
                Limpiar filtros
              </button>
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              {gruposFiltrados.map((grupo) => {
                const isOpen = abiertos.has(grupo.cicloId);
                return (
                  <section
                    key={grupo.cicloId}
                    className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 sm:px-6 py-4 bg-gradient-to-r from-sky-50/80 to-white border-b border-gray-100">
                      <div className="min-w-0 flex-1">
                        <h2 className="text-lg font-semibold text-gray-900 truncate" title={grupo.cicloNombre}>
                          {grupo.cicloNombre}
                        </h2>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                          <span>
                            <span className="text-gray-500">Proyecto:</span>{' '}
                            <span className="font-medium text-gray-800">{grupo.proyectoNombre}</span>
                          </span>
                          <span>
                            <span className="text-gray-500">Semana:</span> {grupo.semanaLabel}
                          </span>
                          <span>
                            <span className="text-gray-500">Periodo:</span> {grupo.periodoLabel}
                          </span>
                          <span className="text-gray-500">
                            {grupo.dias.length} {grupo.dias.length === 1 ? 'día con salida' : 'días con salida'}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleCiclo(grupo.cicloId)}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-sky-200 bg-white px-4 py-2.5 text-sm font-medium text-sky-900 shadow-sm hover:bg-sky-50 transition-colors shrink-0"
                        aria-expanded={isOpen}
                      >
                        {isOpen ? (
                          <>
                            <span>Ocultar días</span>
                            <svg
                              className="w-4 h-4 rotate-180 transition-transform"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </>
                        ) : (
                          <>
                            <span>Ver días</span>
                            <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </>
                        )}
                      </button>
                    </div>

                    {isOpen && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                              <th className="px-4 sm:px-6 py-3 whitespace-nowrap">Fecha</th>
                              <th className="px-4 sm:px-6 py-3 whitespace-nowrap">Día</th>
                              <th className="px-4 sm:px-6 py-3 whitespace-nowrap">Estado</th>
                              <th className="px-4 sm:px-6 py-3 text-right whitespace-nowrap">Acción</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {grupo.dias.map((row) => (
                              <tr key={`${row.cicloId}-${row.diaKey}`} className="hover:bg-gray-50/80">
                                <td className="px-4 sm:px-6 py-3 text-gray-900 tabular-nums">
                                  {row.fechaYmd ? (
                                    <>
                                      <span className="font-medium">{row.fechaYmd}</span>
                                      <span className="ml-2 text-gray-500 text-xs">{formatearDiaCorto(row.fechaYmd)}</span>
                                    </>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="px-4 sm:px-6 py-3 text-gray-800 font-medium">{row.diaLabel}</td>
                                <td className="px-4 sm:px-6 py-3">
                                  <BadgeEstado />
                                </td>
                                <td className="px-4 sm:px-6 py-3 text-right">
                                  <Link
                                    to={`${basePath}/ciclo/${encodeURIComponent(row.cicloId)}/dia/${encodeURIComponent(row.diaKey)}`}
                                    className="inline-flex rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-sky-700"
                                  >
                                    Ver detalle
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DespachoIndex;
